// API client for Cloudflare Worker backend
// Worker is the source of truth (snake_case), this client converts to camelCase

import type {
  AuthSession,
  Plan,
  Profile,
  WorkerAuthResponse,
  WorkerMeResponse,
  WorkerPlan,
  WorkerProfileResponse,
  VoucherValidationResult,
} from '@/types';
import { toUser, toPlan, toProfile } from '@/types';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(error.message ?? `HTTP ${res.status}`);
  }
  return res.json();
}

function setTokens(data: WorkerAuthResponse) {
  localStorage.setItem('access_token', data.access_token);
  localStorage.setItem('refresh_token', data.refresh_token);
  localStorage.setItem('access_expires', String(Date.now() + data.expires_in * 1000));
  localStorage.setItem('refresh_expires', String(Date.now() + data.refresh_expires_in * 1000));
}

function clearTokens() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('access_expires');
  localStorage.removeItem('refresh_expires');
}

async function refreshIfNeeded(): Promise<void> {
  const refreshExpires = localStorage.getItem('refresh_expires');
  if (!refreshExpires) return;
  if (Date.now() < Number(refreshExpires) - 60_000) return; // 1 min buffer

  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) throw new Error('No refresh token');

  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!res.ok) {
    clearTokens();
    throw new Error('Session expired');
  }

  const data: WorkerAuthResponse = await res.json();
  setTokens(data);
}

export const api = {
  // Auth
  async login(email: string, password: string): Promise<AuthSession> {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data: WorkerAuthResponse = await handleResponse(res);
    setTokens(data);
    return { user: toUser(data.user), plan: toPlan(data.plan) };
  },

  async register(email: string, password: string, displayName?: string): Promise<void> {
    const res = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, display_name: displayName }),
    });
    await handleResponse(res);
  },

  async forgotPassword(email: string): Promise<void> {
    const res = await fetch(`${API_BASE}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    await handleResponse(res);
  },

  async resetPassword(token: string, password: string): Promise<void> {
    const res = await fetch(`${API_BASE}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
    await handleResponse(res);
  },

  async logout(): Promise<void> {
    const res = await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    });
    await handleResponse(res);
    clearTokens();
  },

  async getMe(): Promise<AuthSession> {
    await refreshIfNeeded();
    const res = await fetch(`${API_BASE}/auth/me`, {
      headers: getAuthHeaders(),
    });
    const data: WorkerMeResponse = await handleResponse(res);
    return { user: toUser(data.user), plan: toPlan(data.plan) };
  },

  // Plans
  async getPlans(): Promise<Plan[]> {
    const res = await fetch(`${API_BASE}/plans`, { headers: getAuthHeaders() });
    const data = await handleResponse<{ plans: WorkerPlan[] }>(res);
    return data.plans.map(toPlan);
  },

  // User profile & settings
  async getProfile(): Promise<{ user: Profile; plan: Plan }> {
    const res = await fetch(`${API_BASE}/user/me`, { headers: getAuthHeaders() });
    const data: WorkerProfileResponse = await handleResponse(res);
    return { user: toProfile(data), plan: toPlan(data.plan) };
  },

  async updateProfile(displayName: string): Promise<{ user: Profile; plan: Plan }> {
    const res = await fetch(`${API_BASE}/user/me`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ display_name: displayName }),
    });
    const data: WorkerProfileResponse = await handleResponse(res);
    return { user: toProfile(data), plan: toPlan(data.plan) };
  },

  async getSettings(): Promise<Record<string, unknown>> {
    const res = await fetch(`${API_BASE}/user/me/settings`, { headers: getAuthHeaders() });
    const data = await handleResponse<{ settings: Record<string, unknown> | null }>(res);
    return data.settings ?? {};
  },

  async updateSettings(settings: Record<string, unknown>): Promise<{ success: boolean; settings: Record<string, unknown> }> {
    const res = await fetch(`${API_BASE}/user/me/settings`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ settings }),
    });
    return handleResponse(res);
  },

  async getLoginHistory(): Promise<Array<{ device_info?: any; ip?: string; country?: string; city?: string; region?: string; user_agent?: string; login_at?: number; created_at: number }>> {
    const res = await fetch(`${API_BASE}/user/me/login-history`, { headers: getAuthHeaders() });
    const data = await handleResponse<{ history: any[] }>(res);
    return data.history;
  },

  // Voucher
  async validateVoucher(code: string, userId: string, planId: string, cycle: string): Promise<VoucherValidationResult> {
    const res = await fetch(`${API_BASE}/vouchers/validation`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, user_id: userId, plan_id: planId, cycle }),
    });
    return handleResponse(res);
  },

  async redeemVoucher(code: string, userId: string, planId: string, cycle: string): Promise<VoucherValidationResult> {
    const res = await fetch(`${API_BASE}/vouchers/redemption`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, user_id: userId, plan_id: planId, cycle }),
    });
    return handleResponse(res);
  },

  // SePay
  async createSepayQr(planId: string): Promise<{ qr_url: string; reference: string; amount: number; expires_at: number }> {
    const res = await fetch(`${API_BASE}/sepay/create-qr`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan_id: planId }),
    });
    return handleResponse(res);
  },

  async getSepayPublicConfig(): Promise<{ account_number: string; account_name: string; bank_code: string; template: string }> {
    const res = await fetch(`${API_BASE}/sepay/public-config`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  // Admin (requires admin role)
  async adminGetUsers(page = 1, pageSize = 20, search?: string, plan?: string, status?: string): Promise<{ users: any[]; total: number; page: number; page_size: number }> {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (search) params.set('search', search);
    if (plan) params.set('plan', plan);
    if (status) params.set('status', status);
    const res = await fetch(`${API_BASE}/admin/users?${params}`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  async adminUpdateUserPlan(userId: string, planId: string): Promise<{ success: boolean; plan_id: string; expiry_at: number | null }> {
    const res = await fetch(`${API_BASE}/admin/users/${userId}/plan`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan_id: planId }),
    });
    return handleResponse(res);
  },

  async adminUpdateUserExpiry(userId: string, expiryAt: number | null): Promise<{ success: boolean; expiry_at: number | null }> {
    const res = await fetch(`${API_BASE}/admin/users/${userId}/expiry`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ expiry_at: expiryAt }),
    });
    return handleResponse(res);
  },

  async adminUpdateUserStatus(userId: string, status: string): Promise<{ success: boolean; status: string }> {
    const res = await fetch(`${API_BASE}/admin/users/${userId}/status`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    return handleResponse(res);
  },

  async adminUpdateUserRole(userId: string, role: string): Promise<{ success: boolean; role: string }> {
    const res = await fetch(`${API_BASE}/admin/users/${userId}/role`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    });
    return handleResponse(res);
  },

  async adminGetUserDetail(userId: string): Promise<any> {
    const res = await fetch(`${API_BASE}/admin/users/${userId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },

  async adminClearUserDevice(userId: string): Promise<{ success: boolean }> {
    const res = await fetch(`${API_BASE}/admin/users/${userId}/device`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },

  // Admin Plans
  async adminGetPlans(): Promise<{ plans: any[] }> {
    const res = await fetch(`${API_BASE}/admin/plans`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  async adminUpdatePlan(planId: string, updates: { display_name?: string; features?: string[]; limits?: Record<string, number>; price_monthly?: number }): Promise<{ plan: any }> {
    const res = await fetch(`${API_BASE}/admin/plans/${planId}`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    return handleResponse(res);
  },

  // Admin SePay
  async adminGetSepayConfig(): Promise<{ config: any }> {
    const res = await fetch(`${API_BASE}/admin/config/sepay`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  async adminUpdateSepayConfig(config: { acc: string; bank: string; template: string; holder: string; store: string; webhook_secret?: string; webhook_url: string }): Promise<{ success: boolean; config: any }> {
    const res = await fetch(`${API_BASE}/admin/config/sepay`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
    return handleResponse(res);
  },

  // Admin Audit Logs
  async adminGetAuditLogs(userId?: string, action?: string, page = 1, pageSize = 50): Promise<{ logs: any[]; total: number; page: number; page_size: number }> {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (userId) params.set('user_id', userId);
    if (action) params.set('action', action);
    const res = await fetch(`${API_BASE}/admin/audit-logs?${params}`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  // Admin Vouchers
  async adminGetVouchers(page = 1, pageSize = 20): Promise<{ vouchers: any[]; total: number; page: number; page_size: number }> {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    const res = await fetch(`${API_BASE}/vouchers/admin/vouchers?${params}`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  async adminCreateVoucher(voucher: { code: string; type: 'percent' | 'days' | 'plan_upgrade'; value: number; target_plan?: 'plus' | 'pro' | null; max_uses: number; per_user_limit: number; valid_from: number; valid_until: number; is_active: boolean; metadata?: { applicable_plans?: ('plus' | 'pro')[]; min_amount?: number } }): Promise<{ success: boolean; code: string }> {
    const res = await fetch(`${API_BASE}/vouchers/admin/vouchers`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(voucher),
    });
    return handleResponse(res);
  },

  async adminToggleVoucher(code: string): Promise<{ success: boolean }> {
    const res = await fetch(`${API_BASE}/vouchers/admin/vouchers/${code}/toggle`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },

  async adminDeleteVoucher(code: string): Promise<{ success: boolean }> {
    const res = await fetch(`${API_BASE}/vouchers/admin/vouchers/${code}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },

  async adminUpdateVoucher(code: string, updates: { type?: 'percent' | 'days' | 'plan_upgrade'; value?: number; target_plan?: 'plus' | 'pro' | null; max_uses?: number; per_user_limit?: number; valid_from?: number; valid_until?: number; is_active?: boolean; metadata?: { applicable_plans?: ('plus' | 'pro')[]; min_amount?: number } }): Promise<{ success: boolean }> {
    const res = await fetch(`${API_BASE}/vouchers/admin/vouchers/${code}`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    return handleResponse(res);
  },

  // Token management
  clearTokens,
  isAuthenticated(): boolean {
    const accessExpires = localStorage.getItem('access_expires');
    return !!localStorage.getItem('access_token') && (!accessExpires || Date.now() < Number(accessExpires));
  },
};